import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import Badge from "components/atoms/Badge";
import Image from "components/atoms/Image";
import ProperyList from "components/atoms/PropertyList";
import PriceInfo from "components/atoms/PriceInfo";
import ProductTitle from "../molecules/ProductTitle";
import SupportTeaser from "../molecules/SupportTeaser";
import SearchTracker, {
  TYPE as TRACKING_TYPE,
} from "services/tracking/tracker";

const ResultItemProduct = forwardRef((props, ref) => {
  const {
    additionalLicenses,
    className,
    coverFallbackImg,
    coverUri,
    dateExpectedPublication,
    datePublication,
    detailUri,
    displayTitleHeading,
    displayTitleSubheading,
    displayTitleInfo,
    identifier,
    isbn,
    isEstimatedPrice,
    isFreeInAbo,
    isNew,
    licenseFormShort,
    priceEur,
    publishingHouse,
    schoolSubjects,
    supportedBySeries,
    supportingSeriesData,
    highestQuantityScalePrice,
  } = props;
  const componentBaseClass = "sal-result-item-product";
  const componentClass = classnames(componentBaseClass, className);
  const propertyListItems = [];

  if (additionalLicenses && additionalLicenses.length) {
    propertyListItems.push({
      name: "Auch erhältlich als",
      value: additionalLicenses.join(", "),
    });
  }

  const additionalInformation = [
    publishingHouse,
    isbn ? `ISBN ${isbn}` : undefined,
  ]
    .filter(Boolean)
    .join(", ");

  const supportTeaserData =
    supportedBySeries && supportedBySeries.length > 0 && supportingSeriesData
      ? supportingSeriesData[supportedBySeries[0]]
      : 0;
  const supportLinkUrl = supportTeaserData
    ? new URL(supportTeaserData.linkTarget)
    : undefined;

  if (supportLinkUrl && schoolSubjects && schoolSubjects.length > 0) {
    supportLinkUrl.searchParams.append("fach", schoolSubjects[0]);
  }

  const customSupportTeaserData =
    supportLinkUrl !== undefined
      ? {
          ...supportTeaserData,
          linkTarget: supportLinkUrl.toString(),
        }
      : undefined;

  const badgeLabel =
    highestQuantityScalePrice &&
    `ab ${highestQuantityScalePrice.amount} Stück: ${new Intl.NumberFormat(
      "de-DE",
      {
        style: "currency",
        currency: "EUR",
        currencyDisplay: "code",
      },
    ).format(highestQuantityScalePrice.gross_eurocent / 100)}`;

  const handleSupportBannerClick = () => {
    SearchTracker.track(TRACKING_TYPE.TARGET, {
      trackingData: [
        "Unterstützte Produkte aufrufen",
        `${identifier} - ${displayTitleHeading}`,
        1,
        "s",
      ],
    });
  };

  return (
    <article className={componentClass}>
      <div className={componentBaseClass + "__inner"}>
        <Image
          className={componentBaseClass + "__cover"}
          src={coverUri}
          fallbackImg={coverFallbackImg}
          alt=""
          type="cover"
          role="presentation"
        />
        <div className={componentClass + "__description"}>
          <ProductTitle
            isNew={isNew}
            detailUri={detailUri}
            forwardRef={ref}
            displayTitleHeading={displayTitleHeading}
            displayTitleSubheading={displayTitleSubheading}
            displayTitleInfo={displayTitleInfo}
          />
          <div className={componentClass + "__property-container"}>
            {additionalInformation && (
              <span className={componentClass + "__additional-information"}>
                {additionalInformation}
              </span>
            )}
            {propertyListItems.length > 0 && (
              <ProperyList
                items={propertyListItems}
                className={componentClass + "__properties"}
              />
            )}
            {customSupportTeaserData ? (
              <SupportTeaser
                className={componentClass + "__support-teaser"}
                onClick={handleSupportBannerClick}
                {...customSupportTeaserData}
              />
            ) : null}
          </div>
        </div>
        <div className={componentBaseClass + "__shopping"}>
          <PriceInfo
            amount={priceEur}
            isEstimated={isEstimatedPrice}
            isFreeInAbo={isFreeInAbo}
            dateExpectedPublication={dateExpectedPublication}
            datePublication={datePublication}
            className={componentClass + "__price-info"}
          />
          <div className={componentClass + "__license-info"}>
            {licenseFormShort}
          </div>
          {badgeLabel && (
            <div className={componentClass + "__best-price"}>
              <Badge iconType="Etikett" styleType="round">
                {badgeLabel}
              </Badge>
            </div>
          )}
        </div>
      </div>
    </article>
  );
});

ResultItemProduct.propTypes = {
  additionalLicenses: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  coverFallbackImg: PropTypes.string,
  coverUri: PropTypes.string,
  dateExpectedPublication: PropTypes.string,
  datePublication: PropTypes.string,
  detailUri: PropTypes.string.isRequired,
  displayTitleHeading: PropTypes.string.isRequired,
  displayTitleInfo: PropTypes.arrayOf(PropTypes.string),
  displayTitleSubheading: PropTypes.arrayOf(PropTypes.string),
  identifier: PropTypes.string,
  isbn: PropTypes.string,
  isEstimatedPrice: PropTypes.bool,
  isFreeInAbo: PropTypes.bool,
  isNew: PropTypes.bool,
  licenseFormShort: PropTypes.string,
  priceEur: PropTypes.number,
  publishingHouse: PropTypes.string,
  schoolSubjects: PropTypes.arrayOf(PropTypes.string),
  supportedBySeries: PropTypes.arrayOf(PropTypes.string),
  supportingSeriesData: PropTypes.objectOf(
    PropTypes.shape({
      linkLabel: PropTypes.string,
      linkTarget: PropTypes.string,
      teaserImageSrc: PropTypes.string,
    }),
  ),
  highestQuantityScalePrice: PropTypes.objectOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      gross_eurocent: PropTypes.number.isRequired,
    }).isRequired,
  ),
};

ResultItemProduct.displayName = "ResultItemProduct";

export default ResultItemProduct;
